// @ts-strict-ignore
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';

/**
 * Service to reload the current route, currently used in our error handling.
 */
@Injectable({ providedIn: 'root' })
export class RouteReloadService {
  private _router = inject(Router);

  private _routeToReload: ActivatedRoute;

  /**
   * Finds the "leaf route" in the activated router state tree.
   * Is called by the error handler whenever it picks up an error,
   */
  prepareRouteForReload(): void {
    const routerState = this._router.routerState;
    let route = routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    this._routeToReload = route;
  }

  /**
   * Sets the reloadCurrentRoute property on the route to reload and then navigates
   * to the current url (onSameUrlNavigation: 'reload' is a requirement). Also adds
   * a param to the current route to trigger paramChange, making sure resolvers run.
   *
   * This is working in conjunction with CustomRouteReuseStrategy.shouldReuseRoute,
   * which compare current and future ActivatedRouteSnapshots and determines if a route
   * should be reused or reloaded.
   *
   * @see https://angular.io/api/router/OnSameUrlNavigation#description
   */
  reloadCurrentRoute(): void {
    setReloadFlagOnActivatedRouteSnapshot(this._router.routerState.snapshot.root, this._routeToReload);
    this._router.navigate([this._router.url], { onSameUrlNavigation: 'reload' });
  }
}

const setReloadFlagOnActivatedRouteSnapshot = (
  routeInTree: ActivatedRouteSnapshot,
  routeToReload: ActivatedRoute
): boolean => {
  if (routeInTree?.routeConfig === routeToReload?.routeConfig) {
    routeInTree.params = { ...routeInTree.params, triggerParamsChangeToRunResolvers: true };
    routeInTree.data = { ...routeInTree.data, reloadCurrentRoute: true };
    return true;
  } else if (routeInTree.firstChild) {
    setReloadFlagOnActivatedRouteSnapshot(routeInTree.firstChild, routeToReload);
  } else {
    return false;
  }
};
